import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'

export interface LayerStyleOptions {
  scale?: number
  includeTransition?: boolean
  isInherited?: boolean
}

/**
 * Shared layer styling utilities
 * Used across PresentView, DisplayPreview, and CanvasEditor
 */
export function useLayerStyles() {
  /**
   * Get base positioning and sizing styles for a layer
   */
  const getLayerStyle = (layer: DisplayLayer, options: LayerStyleOptions = {}) => {
    const { scale = 1, includeTransition = false, isInherited = false } = options
    
    const baseStyle: Record<string, any> = {
      left: `${layer.x * scale}px`,
      top: `${layer.y * scale}px`,
      width: `${layer.width * scale}px`,
      height: `${layer.height * scale}px`,
      mixBlendMode: layer.effects?.blendMode || 'normal',
      // No zIndex - rely purely on DOM order for stacking
      isolation: 'isolate' // Isolate stacking context so tint doesn't affect layers below
    }
    
    const animation = layer.effects?.animation
    if (animation?.enabled) {
      return {
        ...baseStyle,
        '--layer-rotation': `${layer.effects?.rotation || 0}deg`,
        '--animation-speed': `${animation.speed || 1}s`,
        '--animation-amplitude': animation.amplitude || 1
      }
    }
    
    return {
      ...baseStyle,
      transform: `rotate(${layer.effects?.rotation || 0}deg)`
    }
  }

  /**
   * Get CSS filter string for color adjustments
   * @param scale - Optional scale factor to apply to pixel-based filters (like blur)
   */
  const getFilterStyles = (layer: DisplayLayer, scale: number = 1): string | undefined => {
    const filters: string[] = []
    const effects = layer.effects || {}

    if (effects.hue !== undefined && effects.hue !== 0) {
      filters.push(`hue-rotate(${effects.hue}deg)`)
    }
    if (effects.saturation !== undefined && effects.saturation !== 1) {
      filters.push(`saturate(${effects.saturation})`)
    }
    if (effects.brightness !== undefined && effects.brightness !== 1) {
      filters.push(`brightness(${effects.brightness})`)
    }
    if (effects.contrast !== undefined && effects.contrast !== 1) {
      filters.push(`contrast(${effects.contrast})`)
    }
    if (effects.blur !== undefined && effects.blur > 0) {
      // Scale blur based on display scale for consistent appearance
      filters.push(`blur(${effects.blur * scale}px)`)
    }

    return filters.length > 0 ? filters.join(' ') : undefined
  }

  /**
   * Get CSS clip-path for crop settings
   */
  const getCropStyles = (layer: DisplayLayer): string | undefined => {
    if (!layer.crop) return undefined
    const { top, left, right, bottom } = layer.crop

    if (top === 0 && left === 0 && right === 0 && bottom === 0) return undefined
    return `inset(${top}% ${right}% ${bottom}% ${left}%)`
  }

  /**
   * Get combined styles for image elements
   * @param scale - Optional scale factor to apply to pixel-based filters (like blur)
   */
  const getImageStyle = (layer: DisplayLayer, scale: number = 1) => {
    const style: Record<string, any> = {
      filter: getFilterStyles(layer, scale),
      clipPath: getCropStyles(layer)
    }
    
    if (layer.effects?.opacity !== undefined && layer.effects.opacity !== 1) {
      style.opacity = layer.effects.opacity
    }
    
    return style
  }

  /**
   * Get combined styles for video elements
   * @param scale - Optional scale factor to apply to pixel-based filters (like blur)
   */
  const getVideoStyle = (layer: DisplayLayer, scale: number = 1) => {
    const style: Record<string, any> = {
      filter: getFilterStyles(layer, scale),
      clipPath: getCropStyles(layer)
    }
    
    if (layer.effects?.opacity !== undefined && layer.effects.opacity !== 1) {
      style.opacity = layer.effects.opacity
    }
    
    return style
  }

  /**
   * Get animation class name
   */
  const getAnimationClass = (layer: DisplayLayer): string => {
    const animation = layer.effects?.animation
    if (!animation?.enabled) return ''
    return `animate-${animation.type}`
  }

  /**
   * Get scroll container specific styles
   */
  const getScrollContainerStyle = (layer: DisplayLayer) => {
    const animation = layer.effects?.animation
    if (!animation?.enabled || animation.type !== 'scroll') return {}
    
    return {
      '--animation-speed': `${animation.speed || 10}s`,
    } as Record<string, string>
  }

  /**
   * Get crop overlay style for editor
   */
  const getCropOverlayStyle = (layer: DisplayLayer) => {
    if (!layer.crop) return null

    const { top, left, right, bottom } = layer.crop

    if (top === 0 && left === 0 && right === 0 && bottom === 0) {
      return null
    }

    return {
      top: `${top}%`,
      left: `${left}%`,
      right: `${right}%`,
      bottom: `${bottom}%`
    }
  }

  return {
    getLayerStyle,
    getFilterStyles,
    getCropStyles,
    getImageStyle,
    getVideoStyle,
    getAnimationClass,
    getScrollContainerStyle,
    getCropOverlayStyle
  }
}
